/** Section identifiers — used as URL fragments and as DOM ids for hash navigation.
 *  `LOGISTICS` and `OTHER` are hardcoded as deep-link targets by the "Getting Started" onboarding
 *  tasks (`enterprise-home/_logic/task-registry.ts`) — keep those two id strings stable even as
 *  their field content changes. */
export const SECTION_IDS = {
  LOGISTICS: "logistics",
  HEADCOUNT: "headcount",
  EVENT_DATES: "event-dates",
  ROOMS: "rooms",
  MEETING_ROOMS: "meeting-rooms",
  AGENDA: "agenda",
  BUDGET_DECISION: "budget-decision",
  OTHER: "other",
  USERS: "users",
} as const;

export type SectionId = (typeof SECTION_IDS)[keyof typeof SECTION_IDS];

/** Section nav metadata — order here drives both side-nav and content order. Mirrors the
 *  create-event-v3 wizard's step order (About-your-event → Dates → Headcount → Guestrooms →
 *  Meeting rooms → Agenda → Budget & decision → Final details). */
export const SECTIONS: ReadonlyArray<{
  id: SectionId;
  label: string;
  required: boolean;
}> = [
  { id: SECTION_IDS.LOGISTICS, label: "Logistics", required: true },
  { id: SECTION_IDS.HEADCOUNT, label: "Headcount", required: false },
  { id: SECTION_IDS.EVENT_DATES, label: "Event dates", required: false },
  { id: SECTION_IDS.ROOMS, label: "Guestrooms", required: false },
  { id: SECTION_IDS.MEETING_ROOMS, label: "Meeting rooms", required: false },
  { id: SECTION_IDS.AGENDA, label: "Agenda", required: false },
  { id: SECTION_IDS.BUDGET_DECISION, label: "Budget & decision", required: false },
  { id: SECTION_IDS.OTHER, label: "Other", required: false },
  { id: SECTION_IDS.USERS, label: "Users", required: false },
];

/**
 * Budget categories stored as keys on `events.budget_breakdown`. The order
 * here drives the rendered grid in the Logistics section. Labels match the
 * existing `BudgetBreakdownSection` so users see consistent copy across
 * read and edit surfaces.
 */
export const BUDGET_CATEGORIES = [
  { key: "venue", label: "Venue" },
  { key: "fb", label: "Food & beverage" },
  { key: "av", label: "Audio / visual" },
  { key: "transport", label: "Transport" },
  { key: "flight", label: "Flights" },
  { key: "activities", label: "Activities" },
  { key: "merch", label: "Merchandise" },
  { key: "other", label: "Other" },
] as const;

export {
  DECISION_FACTOR_OPTIONS,
  DECISION_FACTOR_OTHER_VALUE,
  filterDecisionFactors,
  resolveDecisionFactorOtherText,
} from "@/lib/events/decision-factor-options";

/**
 * Additional service options — shared with the create-event-v3 wizard via `@/lib/events` so `/details`
 * reads/writes the same `events.additional_services` values instead of maintaining a second copy that
 * could drift.
 */
export { ADDITIONAL_SERVICE_OPTIONS, filterAdditionalServices } from "@/lib/events/additional-service-options";

/**
 * Event-source options — shared with the create-event-v3 wizard via `@/lib/events` so `/details`
 * reads/writes the same `events.event_source` values instead of maintaining a second copy that
 * could drift.
 */
export { EVENT_SOURCE_OPTIONS, EVENT_SOURCE_OTHER_VALUE } from "@/lib/events/event-source-options";

/**
 * Reuses the existing `event_attachments` bucket already used by the
 * create-event RFP builder. Per-event details files are namespaced under
 * an `event_details/` prefix so they're easy to distinguish from RFP
 * uploads that the wizard writes at `draft_event_<id>/...`.
 */
export const EVENT_DOCUMENTS_BUCKET = "event_attachments";

/** Most location chips one save may carry. The removal ledger is capped to the same number per pass. */
export const MAX_LOCATION_CHIPS = 25;

/**
 * Longest `formatted_address` a new location chip may carry into the jsonb.
 * A Google formatted address is well under this; it bounds a crafted client
 * posting straight to the server action.
 */
export const MAX_FORMATTED_ADDRESS_LENGTH = 255;

/**
 * How many location-removal ledger rows the pending read fetches per page.
 * The read pages until the rows run out, so this is a round-trip size, not a
 * window: no pending row is ever past it.
 */
export const LEDGER_PAGE_SIZE = 200;

/**
 * Most pages the pending read will fetch before failing closed. Every ledger
 * row is a real removal of a city with hotels to tell (a drop that reaches
 * nobody writes none), so an event past this has something wrong with it,
 * and a partial read could hide a pending city; the read reports it instead.
 */
export const LEDGER_MAX_PAGES = 50;

/**
 * Strict path layout enforced on the server:
 *   `event_details/<eventId>/<userId>/<timestamp>_<sanitizedName>.<ext>`
 *
 * The numeric segments require a leading non-zero digit so two paths that
 * differ only by leading zeros can't both round-trip through `Number()`.
 */
export const STORAGE_PATH_REGEX = /^event_details\/([1-9]\d*)\/([1-9]\d*)\/[1-9]\d*_[a-zA-Z0-9._-]+\.[a-zA-Z0-9]+$/;

/** Upload limits. PDFs are most common; we accept common office docs and images too. */
export const UPLOAD_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
export const UPLOAD_MAX_FILE_NAME_LENGTH = 200;
export const UPLOAD_MAX_DOCS_PER_EVENT = 25;
/** Cap on unlinked draft uploads a single user can accumulate under `event_details/draft/<userId>/`
 *  before an event exists to attach them to. Mirrors {@link UPLOAD_MAX_DOCS_PER_EVENT}. */
export const UPLOAD_MAX_DRAFT_DOCS_PER_USER = 25;
/** Concurrency cap when the user drops many files at once. Keeps the auth+access RPCs from melting. */
export const UPLOAD_MAX_CONCURRENT = 3;

export const UPLOAD_ACCEPT_ATTR = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.txt,.csv";

/**
 * z-index for the document delete confirmation dialog. The editor embeds in the
 * RFP edit-details modal (rfp-confirmation `EDIT_MODAL_Z_INDEX = 99993`, content
 * at +1), so the default `z-50` dialog would render trapped behind it. This sits
 * above that modal and below the toast viewport (`z-[100001]`) so delete
 * success/error toasts still layer on top. On the standalone details page
 * nothing competes at this level, so the high value is harmless there.
 */
export const DOCUMENT_DELETE_DIALOG_Z_INDEX_CLASS = "z-[99996]";

export const UPLOAD_VALID_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png",
  "image/jpeg",
  "text/plain",
  "text/csv",
] as const;

export const UPLOAD_VALID_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".png",
  ".jpg",
  ".jpeg",
  ".txt",
  ".csv",
] as const;

/** Auto-save debounce delay. Long enough to coalesce typing, short enough to feel responsive. */
export const AUTO_SAVE_DELAY_MS = 1500;

/**
 * Upper bound on `useEventDetailsAutoSave`'s flush drain loop. Each pass is one
 * save round-trip; a flush settles in one or two, so this only trips if a save
 * keeps failing or the user keeps editing through it — then flush reports
 * failure rather than looping forever.
 */
export const FLUSH_MAX_ITERATIONS = 10;

/** React Query keys for client-side event-details fetches (e.g. the RFP edit popup). */
export const eventDetailsQueryKey = {
  bundle: (eventId: number) => ["event-details", "bundle", eventId] as const,
};

/**
 * Sticky page-header heights for the details editor (px). The sticky side-nav
 * top offset and the scroll-to-section offset both derive from the active value
 * so the standalone page (full) and the embedded RFP popup (compact) each stay
 * internally aligned. Compact trims the full-page hero so it doesn't eat a
 * disproportionate share of the modal's limited height.
 */
export const EVENT_DETAILS_HEADER_HEIGHT_PX = 160;
export const EVENT_DETAILS_HEADER_HEIGHT_COMPACT_PX = 96;

/**
 * PostgreSQL `smallint` upper bound. Caps client-supplied counts persisted to
 * smallint columns (e.g. `events.num_meeting_rooms_needed`) so an out-of-range
 * value can't blow up the insert/update with a 22003 error.
 */
export const SMALLINT_MAX = 32767;

/** Milliseconds in one day. Wall-clock UTC math across the rooms / agenda
 *  date plumbing (range pickers, day-column derivation, `getNightsBetween`,
 *  `addDays`). Single source of truth so one tweak can't drift the others. */
export const ONE_DAY_MS = 86_400_000;

/** Pixel height per hour on the Agenda time grid. 56 keeps a 15-min slot at 14px — comfortable touch target. */
export const AGENDA_HOUR_PX = 56;

/** Hour-gutter (HH:MM labels) column width in the agenda grid. Shared
 *  between WeekGrid, MobileDayTabs, and HourGutter so a layout tweak
 *  can't drift the alignment. */
export const AGENDA_HOUR_GUTTER_WIDTH_PX = 56;

/** Minimum per-column width in the week grid. Keeps day columns readable
 *  before horizontal scroll kicks in (>5 days). */
export const AGENDA_COLUMN_MIN_WIDTH_PX = 220;

/** Quantize drag/resize to 15-minute increments. Matches Google Calendar's default. */
export const AGENDA_SNAP_MINUTES = 15;

/** Floor for resize-end. Sessions can't shrink below this. */
export const AGENDA_MIN_SESSION_MINUTES = 15;

/** Pointer movement threshold (px) below which a pointerup is treated as a click, not a drag. */
export const AGENDA_DRAG_THRESHOLD_PX = 5;

/** Below this viewport width, the agenda falls back to single-day tabs. */
export const AGENDA_MOBILE_BREAKPOINT_PX = 768;

/**
 * Visible time-grid range: 6 AM through midnight. Excludes the rarely-used
 * overnight hours so the calendar doesn't dominate the page with empty
 * 12 AM – 6 AM dead space. The grid renders the full 18-hour window inside
 * a fixed-height scrollable container — sessions outside this range are
 * not rendered (legacy data with overnight times survives on disk but
 * doesn't appear visually).
 */
export const AGENDA_GRID_START_HOUR = 6;
export const AGENDA_GRID_END_HOUR = 24;
export const AGENDA_GRID_HOURS = AGENDA_GRID_END_HOUR - AGENDA_GRID_START_HOUR;
export const AGENDA_GRID_START_MINUTES = AGENDA_GRID_START_HOUR * 60;
export const AGENDA_GRID_END_MINUTES = AGENDA_GRID_END_HOUR * 60;

/** Total intrinsic height of the time grid. Derived so layout math doesn't drift. */
export const AGENDA_DAY_HEIGHT_PX = AGENDA_HOUR_PX * AGENDA_GRID_HOURS;

/** Fixed visible height of the calendar body — content scrolls inside this window. */
export const AGENDA_VISIBLE_HEIGHT_PX = 640;

/** Width of the GCal-style session form popover (px). */
export const AGENDA_POPOVER_WIDTH_PX = 420;
/** Gap between the popover and its anchor edge (px). */
export const AGENDA_POPOVER_ANCHOR_GAP_PX = 12;
/** Min visual breathing room between the popover and any viewport edge (px). */
export const AGENDA_POPOVER_VIEWPORT_PADDING_PX = 16;

/** Cap on Sentry warnings emitted by a single `readAgendaItems` call. */
export const AGENDA_MALFORMED_WARN_LIMIT = 5;
/** Defensive ceiling on input array length — guard against corrupted jsonb. */
export const AGENDA_MAX_ENTRIES = 1000;

/** Hard cap on agenda date-range count + per-range span and per-session
 *  date-array length, enforced at the server action boundary. Mirrors the
 *  page-time `MAX_NIGHTS` cap so a crafted client can't force the server
 *  to allocate an unbounded date-union set. See `serializeAgendaDateRanges`
 *  and `parseAgendaItemShape`. */
export const AGENDA_MAX_RANGES = 50;
export const AGENDA_MAX_RANGE_DAYS = 366;
export const AGENDA_MAX_SESSION_DATES = 50;

/** Hard cap on ranked `events.event_dates` option count + per-option span, enforced at the
 *  server action boundary. Same rationale as `AGENDA_MAX_RANGES`/`AGENDA_MAX_RANGE_DAYS`. See
 *  `serializeEventDateOptions`. */
export const EVENT_DATES_MAX_OPTIONS = 20;
export const EVENT_DATES_MAX_SPAN_DAYS = 366;

// The meeting-room detail-grid day cap moved to `components/ui/MeetingRoomsDayToggle.tsx`
// (it's a presentation-layer concern, not business logic, and that component is now
// shared cross-feature).

/** Defensive caps on `custom_questions`/`custom_concessions`, enforced at the server action
 *  boundary — mirrors the agenda date-range caps so a crafted client can't force an unbounded jsonb
 *  write. */
export const CUSTOM_LIST_MAX_ITEMS = 50;
export const CUSTOM_LIST_MAX_ITEM_LENGTH = 500;

/** Defensive cap on free-text notes fields (`other_notes`, `notes_location_budget`), enforced at
 *  the server action boundary for the same reason as {@link CUSTOM_LIST_MAX_ITEM_LENGTH}. */
export const NOTES_MAX_LENGTH = 5000;

/**
 * Width of the date-label column on the left of each preview row (px).
 * The hour-scale legend pads its left side by this width + the row gap
 * so the labels align with the timeline bars below. Keep these two in
 * sync via {@link AGENDA_PREVIEW_HOUR_SCALE_OFFSET_PX}.
 */
export const AGENDA_PREVIEW_DATE_COL_WIDTH_PX = 94;
/** Left offset for the hour-scale legend = date column + row gap (16). */
export const AGENDA_PREVIEW_HOUR_SCALE_OFFSET_PX = AGENDA_PREVIEW_DATE_COL_WIDTH_PX + 16;
/**
 * Minimum rendered width of a session chip in the preview timeline (px).
 * Short sessions (e.g. a 15-minute break) would otherwise be a sliver of
 * the ~18-hour visible window, truncating their title to 2-3 characters.
 * Applied as CSS `min-width` alongside the duration-proportional `width`
 * percentage, so it only kicks in for sessions that would render smaller.
 */
export const AGENDA_PREVIEW_BLOCK_MIN_WIDTH_PX = 56;

/**
 * Stored verbatim in `events.flexibility` when the user answers "No" to the
 * Date flexibility prompt. Human-readable so display surfaces (AVA inbox,
 * chain-rep dashboard, RFP email templates) render coherent prose without
 * needing to recognize the sentinel. The Logistics editor and
 * `enterprise-sourcing-search` (truthiness check + RFP email template) are
 * the only call sites that special-case it.
 */
export const FLEXIBILITY_NOT_FLEXIBLE_SENTINEL = "Dates not flexible";

/**
 * The room type that always renders in the Rooms section table — even when
 * no values exist. Other types appear progressively via "+ Add room type"
 * or are derived from saved non-zero values on initial load.
 */
export const ANCHORED_ROOM_TYPE = "Single" as const;

/** Max length for the event display name. Matches contract-name policy for consistency across rename UIs. */
export const EVENT_NAME_MAX_LENGTH = 100;

/** SaveStatusBadge re-renders on this interval so the relative time stays current. */
export const SAVE_STATUS_REFRESH_INTERVAL_MS = 30_000;

/** TTL of signed download URLs minted for documents. Just long enough to click through. */
export const SIGNED_DOWNLOAD_TTL_SECONDS = 60;

/**
 * Shown when leaving the details page with uncommitted edits. `beforeunload`
 * ignores custom text (every browser shows its own wording), but the in-app
 * navigation prompt uses it verbatim, so it is worded for that surface.
 */
export const UNSAVED_CHANGES_PROMPT = "You have unsaved changes. Leave without saving them?";

/**
 * Copy for the in-app unsaved-changes dialog, which covers every way off the
 * page except tab close and reload (the browser owns that prompt). The filled
 * button is the safe choice, staying, so a reflex click loses nothing.
 */
export const UNSAVED_CHANGES_DIALOG = {
  title: "Unsaved changes",
  message: "You have unsaved changes on this page. Leave now and they will be lost.",
  leave: "Leave without saving",
  stay: "Keep editing",
} as const;

/**
 * The server's refusal when a save that drops a location cannot find out
 * which hotels that reaches. The save is held rather than guessed at: a
 * permission granted on a failed read is a city whose hotels are never told.
 * Names the location change only: the other sections save in parallel
 * (`flushAll`) and may well have landed. The gate's own toast for a failed
 * preview (in `useLocationRemovalGate`) runs before any section saves, so it
 * can say nothing was saved; this one cannot.
 */
export const REMOVAL_CHECK_FAILED =
  "Couldn't check which hotels this affects, so the location change was not saved. Try again.";

/**
 * The send's refusal for a caller who may edit the event but not tell its
 * hotels, worded for where it is shown: after the save. The send only runs
 * once the chip is off the event and its removal recorded, so unlike the
 * gate's and the write's `REMOVAL_NEEDS_OWNER` (where the locations were not
 * saved) this one says the removal stands and only the telling is refused.
 * The removal stays pending, and an owner's next save offers it again.
 */
export const REMOVAL_SEND_NEEDS_OWNER =
  "The location was removed, but hotels there already have your RFP, so only the event owner or a Nowadays admin can tell them.";

/** The refusal for a location list that is not a bounded list of chips, on the write and on the preview alike. */
export const INVALID_LOCATIONS = "Invalid locations.";
